import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogActions, Button, TextField, Typography, Box } from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '../../common/icons/close';
import { getFieldLabel, objectToString } from '../eligibilityAgentUtils';

import {
  FontTextXlNormal,
  FontTextBaseBold,
  FontTextBaseNormal,
  FontTextSmSemibold,
  PrimitivesWhite,
} from '@ambulnz/design-tokens';

const ArrowIcon = () => (
  <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.9792 7.83334H0.833344V6.16668H10.9792L6.31251 1.50001L7.50001 0.333344L14.1667 7.00001L7.50001 13.6667L6.31251 12.5L10.9792 7.83334Z"
      fill="#4E677E"
    />
  </svg>
);

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '4px',
    width: '800px',
    maxWidth: '800px',
    margin: '16px',
    boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.15)',
    padding: 0,
    backgroundColor: PrimitivesWhite,
  },
});

const MainContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  padding: '24px',
  width: '100%',
});

const TitleSection = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  alignSelf: 'stretch',
  gap: '10px',
});

const Title = styled(Typography)({
  ...FontTextXlNormal,
  fontWeight: 600,
  fontSize: '20px',
  lineHeight: '1.171875em',
  color: '#131E29',
  margin: 0,
});

const CloseButton = styled('button')({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
});

const Section = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  alignSelf: 'stretch',
  gap: '16px',
  padding: '16px',
  backgroundColor: '#EFF3F6',
  borderRadius: '8px',
});

const ChangesSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  alignSelf: 'stretch',
  gap: '24px',
  padding: '16px',
  backgroundColor: '#EFF3F6',
  borderRadius: '8px',
});

const SectionTitle = styled(Typography)({
  ...FontTextBaseBold,
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '1.5em',
  color: '#131E29',
  textAlign: 'left',
  alignSelf: 'stretch',
});

const SectionContent = styled(Typography)({
  ...FontTextBaseNormal,
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '2em',
  color: '#131E29',
  textAlign: 'left',
  alignSelf: 'stretch',
});

const ChangesTable = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'stretch',
  gap: '16px',
});

const TableHeader = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '87px 1fr 1fr',
  gap: '32px',
  alignSelf: 'stretch',
});

const TableRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '87px 1fr 1fr',
  gap: '32px',
  alignSelf: 'stretch',
  alignItems: 'start',
});

const ColumnHeader = styled(Typography)({
  ...FontTextBaseBold,
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '1.5em',
  color: '#131E29',
  textAlign: 'left',
});

const RowLabel = styled(Typography)({
  ...FontTextBaseNormal,
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '1.5em',
  color: '#4E677E',
  textAlign: 'left',
});

const ColumnValue = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'flex-start',
});

const ValueText = styled(Typography)({
  ...FontTextBaseNormal,
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '1.5em',
  color: '#131E29',
  textAlign: 'left',
});

const MessageRow = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
  backgroundColor: '#FFF9E6',
  borderRadius: '6px',
  borderLeft: '4px solid #F59E0B',
});

const ArrowContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '4px',
  flexShrink: 0,
});

const ButtonContainer = styled(DialogActions)({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  alignSelf: 'stretch',
  gap: '16px',
  padding: '0 24px 24px',
});

const SecondaryButton = styled(Button)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0 24px',
  height: '36px',
  backgroundColor: 'transparent',
  border: '1px solid #0D2B59',
  borderRadius: '6px',
  ...FontTextSmSemibold,
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '1.4285714285714286em',
  color: '#0D2B59',
  textTransform: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: 'rgba(13, 45, 89, 0.04)',
    border: '1px solid #0D2B59',
  },
});

const PrimaryButton = styled(Button)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0 24px',
  height: '36px',
  backgroundColor: '#0D2B59',
  borderRadius: '6px',
  ...FontTextSmSemibold,
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '1.4285714285714286em',
  color: '#FFFFFF',
  textTransform: 'none',
  border: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: '#21569c',
  },
  '&:disabled': {
    backgroundColor: '#d4d4d8',
    color: '#a1a1aa',
  },
});

const RejectReasonDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '4px',
    width: '480px',
    maxWidth: '480px',
    padding: 0,
    backgroundColor: PrimitivesWhite,
  },
});

const RejectReasonContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '24px',
});

const ValidationMessage = styled(Typography)({
  ...FontTextBaseNormal,
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '1.4em',
  color: '#C62828',
});

function EligibilityAgentModal({
  open,
  onClose,
  onAccept,
  onReject,
  status,
  appliedRules = [],
  changes = [],
  issues = [],
  coverages,
  coordinationTypes,
  isLoading = false,
  pendingReview = false,
}) {
  const hasChanges = changes && changes.length > 0;
  const hasIssues = issues && issues.length > 0;
  const hasAppliedRules = appliedRules && appliedRules.length > 0;
  const isBlocking = !!pendingReview;

  const [rejectReasonOpen, setRejectReasonOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectReasonError, setRejectReasonError] = useState('');

  useEffect(() => {
    if (!open) {
      setRejectReasonOpen(false);
      setRejectReason('');
      setRejectReasonError('');
    }
  }, [open]);

  const handleDialogClose = (_event, reason) => {
    if (isBlocking) return;
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    if (typeof onClose === 'function') onClose();
  };

  const handleCloseButtonClick = () => {
    if (typeof onClose === 'function') onClose();
  };

  const openRejectReasonDialog = () => {
    setRejectReason('');
    setRejectReasonError('');
    setRejectReasonOpen(true);
  };

  const handleRejectReasonOk = () => {
    const trimmed = rejectReason.trim();
    if (!trimmed) {
      setRejectReasonError('Reject reason is required');
      return;
    }
    if (typeof onReject === 'function') onReject(trimmed);
    setRejectReasonOpen(false);
  };

  const handleRejectReasonChange = e => {
    setRejectReason(e.target.value);
    if (rejectReasonError) setRejectReasonError('');
  };

  const renderChangeRow = (label, beforeValue, afterValue, key) => (
    <TableRow key={key || label}>
      <RowLabel>{label}</RowLabel>
      <ValueText>{beforeValue || '-'}</ValueText>
      <ColumnValue>
        <ArrowContainer>
          <ArrowIcon />
        </ArrowContainer>
        <ValueText>{afterValue || '-'}</ValueText>
      </ColumnValue>
    </TableRow>
  );

  const renderMessageOnlyRow = (message, key) => (
    <MessageRow key={key}>
      <ValueText>{message}</ValueText>
    </MessageRow>
  );

  const renderEvent = (event, index, prefix) => {
    if (!event.key || (!event.before && !event.after)) {
      return renderMessageOnlyRow(event.message || event.name, `${prefix}-${index}`);
    }

    return renderChangeRow(
      getFieldLabel(event.key),
      objectToString(event.before),
      objectToString(event.after),
      `${prefix}-${index}`,
    );
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={handleDialogClose}
        disableEscapeKeyDown={isBlocking}
        aria-labelledby="eligibility-agent-modal-title"
      >
        <MainContent>
          <TitleSection>
            <Title id="eligibility-agent-modal-title">Eligibility Agent Results</Title>
            {!isBlocking && (
              <CloseButton onClick={handleCloseButtonClick} aria-label="Close modal">
                <CloseIcon style={{ width: 20, height: 20, fill: 'currentColor' }} />
              </CloseButton>
            )}
          </TitleSection>

          {hasAppliedRules && (
            <Section>
              <SectionTitle>Rules Applied</SectionTitle>
              <SectionContent>
                {(() => {
                  const uniqueRules = [];
                  const seen = new Set();
                  appliedRules.forEach(rule => {
                    const key = `${rule.name}-${rule.stage}`;
                    if (!seen.has(key)) {
                      seen.add(key);
                      uniqueRules.push(rule);
                    }
                  });
                  return uniqueRules.map((rule, index) => (
                    <React.Fragment key={index}>
                      {rule.name}
                      {index < uniqueRules.length - 1 && <br />}
                    </React.Fragment>
                  ));
                })()}
              </SectionContent>
            </Section>
          )}

          {status && (
            <Section>
              <SectionTitle>
                Eligibility Status: {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
              </SectionTitle>
            </Section>
          )}

          {hasChanges && (
            <ChangesSection>
              <SectionTitle>Changes</SectionTitle>
              <ChangesTable>
                <TableHeader>
                  <Box />
                  <ColumnHeader>Before</ColumnHeader>
                  <ColumnHeader>After</ColumnHeader>
                </TableHeader>

                {changes.map((event, index) => renderEvent(event, index, 'action'))}
              </ChangesTable>
            </ChangesSection>
          )}

          {hasIssues && (
            <ChangesSection>
              <SectionTitle>Issues</SectionTitle>
              <ChangesTable>
                <TableHeader>
                  <Box />
                  <ColumnHeader>Current</ColumnHeader>
                  <ColumnHeader>Expected</ColumnHeader>
                </TableHeader>

                {issues.map((event, index) => renderEvent(event, index, 'issue'))}
              </ChangesTable>
            </ChangesSection>
          )}
        </MainContent>

        {hasChanges && (
          <ButtonContainer>
            <SecondaryButton onClick={openRejectReasonDialog} disabled={isLoading}>
              Reject Changes
            </SecondaryButton>
            <PrimaryButton onClick={onAccept} disabled={isLoading}>
              {isLoading ? 'Applying...' : 'Accept Changes'}
            </PrimaryButton>
          </ButtonContainer>
        )}
      </StyledDialog>

      <RejectReasonDialog
        open={rejectReasonOpen}
        onClose={() => setRejectReasonOpen(false)}
        aria-labelledby="reject-reason-dialog-title"
      >
        <RejectReasonContent>
          <Title id="reject-reason-dialog-title">Please Input Reject Reason</Title>
          <TextField
            multiline
            minRows={3}
            value={rejectReason}
            onChange={handleRejectReasonChange}
            placeholder="Enter a reason"
            inputProps={{ 'aria-label': 'reject reason', 'aria-required': 'true' }}
            error={!!rejectReasonError}
            required
            fullWidth
          />
          {rejectReasonError && (
            <ValidationMessage role="alert">{rejectReasonError}</ValidationMessage>
          )}
        </RejectReasonContent>
        <ButtonContainer>
          <PrimaryButton onClick={handleRejectReasonOk}>OK</PrimaryButton>
        </ButtonContainer>
      </RejectReasonDialog>
    </>
  );
}

EligibilityAgentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAccept: PropTypes.func,
  onReject: PropTypes.func,
  status: PropTypes.string,
  appliedRules: PropTypes.arrayOf(
    PropTypes.shape({
      stage: PropTypes.string,
      key: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  changes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      before: PropTypes.string,
      after: PropTypes.string,
    }),
  ),
  issues: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
  pendingReview: PropTypes.bool,
};

export default EligibilityAgentModal;
